import React, { useEffect, useRef, useState } from 'react';
import {
  Fab,
  Dialog,
  Box,
  TextField,
  InputAdornment,
  Typography,
} from '@mui/material';
import ApplyWfhQuota from './ApplyQuota/ApplyWfhQuota';
import GenericLoader from '../../../components/Loader/GenericLoader';
import { SHOW_NOTIFICATION, WFHAttribute } from '../../../constants';
import PaginationComponent from '../../../components/Pagination/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import QuotaCardComponents from './QuotaCard/QuotaCardComponents';
import {
  getLeaveQuota,
  getUsersExternalQuota,
  getUsersClientOfficeQuota,
  getUsersWorkTravelQuota,
  getWfhQuota,
} from '../../Services/EmployeePrivilegesService';
import { useDispatch } from 'react-redux';
import ApplyLeavesQuota from './ApplyQuota/ApplyLeavesQuota';
import ApplyExternalQuota from './ApplyQuota/ApplyExternalQuota';

const EmployeePrivilegesComponent = (props) => {
  const { type } = props;
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [data, setData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const dispatch = useDispatch();
  const [totalPage, setTotalPage] = React.useState();
  const [pageNo, setPageNo] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selected, setSelected] = useState('');
  const [isLastActionFocus, setIsLastActionFocus] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const debounceTimeout = useRef(null);
  // Ignores late responses from older list fetches (e.g. default load finishing after a search).
  const fetchRequestIdRef = useRef(0);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    if (selected.length === 0) setIsFocused(false);
  };

  const getActiveSearchString = () => {
    const trimmed = selected.trim();
    return trimmed.length >= 2 ? trimmed : '';
  };

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefresh(!refresh);
  };

  const handlePageSizeChange = (event) => {
    const newPageSize = event.target.value;
    setPageSize(newPageSize);
    pageChange(null, 1);
  };

  const dipatchNotification = (typ, msg) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: {
        open: true,
        type: typ,
        message: msg,
      },
    });
  };
  const tenantId = process.env.REACT_APP_TENANT_ID;
  const year = new Date().getFullYear();

  const getWfhQuotaData = async (searchString, pageSize, pageNumber = pageNo) => {
    const requestId = ++fetchRequestIdRef.current;
    const allWFHQuota = await getWfhQuota(
      tenantId,
      year,
      pageSize,
      pageNumber,
      searchString,
    );
    if (requestId !== fetchRequestIdRef.current) {
      return;
    }
    if (allWFHQuota.isError === false) {
      if (allWFHQuota.data.data.numberOfElements == 0) {
        setPageNo(0);
        setPage(1);
      }
      setLoader(false);
      setTotalPage(allWFHQuota.data.data.totalPages);
      setData(allWFHQuota.data.data.content);

      if (allWFHQuota.status !== 200) {
        dipatchNotification(allWFHQuota.type, allWFHQuota.message);
      }
    }
  };

  const getExternalQuotaData = async (
    searchString,
    pageSize,
    pageNumber = pageNo,
  ) => {
    const requestId = ++fetchRequestIdRef.current;
    const allExternalQuota = await getUsersExternalQuota(
      tenantId,
      year,
      pageSize,
      pageNumber,
      searchString,
    );
    if (requestId !== fetchRequestIdRef.current) {
      return;
    }
    if (allExternalQuota.isError === false) {
      if (allExternalQuota.data.data.numberOfElements == 0) {
        setPageNo(0);
        setPage(1);
      }
      setLoader(false);
      setTotalPage(allExternalQuota.data.data.totalPages);
      setData(allExternalQuota.data.data.content);

      if (allExternalQuota.status !== 200) {
        dipatchNotification(allExternalQuota.type, allExternalQuota.message);
      }
    }
  };

  const getClientOfficeQuotaData = async (
    searchString,
    pageSize,
    pageNumber = pageNo,
  ) => {
    const requestId = ++fetchRequestIdRef.current;
    const allClientOfficeQuota = await getUsersClientOfficeQuota(
      tenantId,
      year,
      pageSize,
      pageNumber,
      searchString,
    );
    if (requestId !== fetchRequestIdRef.current) {
      return;
    }
    if (allClientOfficeQuota.isError === false) {
      if (allClientOfficeQuota.data.data.numberOfElements == 0) {
        setPageNo(0);
        setPage(1);
      }
      setLoader(false);
      setTotalPage(allClientOfficeQuota.data.data.totalPages);
      setData(allClientOfficeQuota.data.data.content);

      if (allClientOfficeQuota.status !== 200) {
        dipatchNotification(
          allClientOfficeQuota.type,
          allClientOfficeQuota.message,
        );
      }
    }
  };

  const getWorkTravelQuotaData = async (
    searchString,
    pageSize,
    pageNumber = pageNo,
  ) => {
    const requestId = ++fetchRequestIdRef.current;
    const allWorkTravelQuota = await getUsersWorkTravelQuota(
      tenantId,
      year,
      pageSize,
      pageNumber,
      searchString,
    );
    if (requestId !== fetchRequestIdRef.current) {
      return;
    }
    if (allWorkTravelQuota.isError === false) {
      if (allWorkTravelQuota.data.data.numberOfElements == 0) {
        setPageNo(0);
        setPage(1);
      }
      setLoader(false);
      setTotalPage(allWorkTravelQuota.data.data.totalPages);
      setData(allWorkTravelQuota.data.data.content);

      if (allWorkTravelQuota.status !== 200) {
        dipatchNotification(
          allWorkTravelQuota.type,
          allWorkTravelQuota.message,
        );
      }
    }
  };

  const getLeaveQuotaData = async (searchString, pageNumber = pageNo) => {
    const requestId = ++fetchRequestIdRef.current;
    const allLeaveQuota = await getLeaveQuota(
      year,
      pageSize,
      pageNumber,
      searchString,
    );
    if (requestId !== fetchRequestIdRef.current) {
      return;
    }
    if (allLeaveQuota.isError === false) {
      if (allLeaveQuota.data.data.numberOfElements === 0) {
        setPageNo(0);
        setPage(1);
      }
      setLoader(false);
      setTotalPage(allLeaveQuota.data.data.totalPages);
      setData(allLeaveQuota.data.data.content);
    }

    if (allLeaveQuota.status !== 200) {
      dipatchNotification(allLeaveQuota.type, allLeaveQuota.message);
    }
  };

  const getExternalQuota = async (searchString, pageSize, pageNumber = pageNo) => {
    const requestId = ++fetchRequestIdRef.current;
    const allWFHQuota = await getUsersExternalQuota(
      tenantId,
      year,
      pageSize,
      pageNumber,
      searchString,
    );
    if (requestId !== fetchRequestIdRef.current) {
      return;
    }

    if (allWFHQuota.data.data.numberOfElements == 0) {
      setPageNo(0);
      setPage(1);
    }
    setLoader(false);
    setTotalPage(allWFHQuota.data.data.totalPages);
    setData(allWFHQuota.data.data.content);

    if (allWFHQuota.status !== 200) {
      dipatchNotification(allWFHQuota.type, allWFHQuota.message);
    }
  };

  useEffect(() => {
    const searchString = getActiveSearchString();
    if (type === 'wfh') {
      getWfhQuotaData(searchString, pageSize);
    } else if (type === 'leave') {
      getLeaveQuotaData(searchString);
    } else if (type === 'clientOffice') {
      getClientOfficeQuotaData(searchString, pageSize);
    } else if (type === 'workTravel') {
      getWorkTravelQuotaData(searchString, pageSize);
    } else {
      getExternalQuota(searchString, pageSize);
    }
  }, [type, refresh]);

  const toggleClose = () => {
    setOpen(false);
  };
  const toggleOpen = () => {
    setOpen(!open);
  };

  const searchEmployeeByName = (str, pageSize, pageNumber = 0) => {
    if (type === 'wfh') {
      getWfhQuotaData(str, pageSize, pageNumber);
    } else if (type === 'leave') {
      getLeaveQuotaData(str, pageNumber);
    } else if (type === 'clientOffice') {
      getClientOfficeQuotaData(str, pageSize, pageNumber);
    } else if (type === 'workTravel') {
      getWorkTravelQuotaData(str, pageSize, pageNumber);
    } else {
      getExternalQuotaData(str, pageSize, pageNumber);
    }
  };

  const handleInputChange = (e) => {
    const inputText = e.target.value;
    setSelected(inputText);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (inputText.trim() !== '' && inputText.length >= 2) {
        setPage(1);
        setPageNo(0);
        searchEmployeeByName(inputText.trim(), pageSize, 0);
      } else if (inputText === '') {
        setPage(1);
        setPageNo(0);
        searchEmployeeByName('', pageSize, 0);
      }
    }, 500);
  };

  return (
    <div>
      {loader ? (
        <div
          style={{
            height: '60vh',
            overflow: 'hidden',
          }}
        >
          <GenericLoader
            rectAttributes={WFHAttribute}
            showError={showError}
            setShowError={setShowError}
          />
        </div>
      ) : (
        <>
          <Box width="105%" style={{ position: 'relative', top: '-45px' }}>
            <TextField
              className="search-select2"
              label="Employee Name / ID"
              name="color"
              onChange={handleInputChange}
              value={isLastActionFocus ? '' : selected}
              onFocus={handleFocus}
              onBlur={handleBlur}
              InputLabelProps={
                !isFocused
                  ? {
                      sx: {
                        transform: 'translate(10%, 25%)',
                      },
                    }
                  : {}
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  '& input': {
                    height: '5px', // Adjust the height value as needed
                  },
                },
              }}
            />
          </Box>
          <Box className="main-content-overlay">
            <div className="main-content-div">
              {data.length !== 0 ? (
                data.map((val, index) => {
                  return (
                    <QuotaCardComponents key={index} data={val} type={type} />
                  );
                })
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '60vh',
                  }}
                >
                  <div>
                    <Typography>{'No Employee Available...'}</Typography>
                  </div>
                </div>
              )}
            </div>
          </Box>
          <Box className="page-holder" sx={{ marginBottom: '10px' }}>
            {data.length !== 0 && (
              <PaginationComponent
                count={totalPage}
                page={page}
                pageChange={pageChange}
                pageSize={pageSize}
                handlePageSizeChange={handlePageSizeChange}
              />
            )}
          </Box>
          <Box className="main-content-overlay">
            <div className="main-content-div">
              <Fab
                color="primary"
                style={{
                  right: 25,
                  bottom: 80,
                  position: 'fixed',
                  zIndex: 1051,
                }}
                onClick={() => {
                  toggleOpen();
                }}
              >
                <AddIcon />
              </Fab>
              <Dialog
                open={open}
                onClose={() => toggleClose()}
                fullWidth
                maxWidth="md"
              >
                {type === 'wfh' && (
                  <ApplyWfhQuota
                    toggleOpen={toggleClose}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    setLoader={setLoader}
                  />
                )}
                {type === 'leave' && (
                  <ApplyLeavesQuota
                    toggleOpen={toggleClose}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    setLoader={setLoader}
                  />
                )}
                {type === 'external' && (
                  <ApplyExternalQuota
                    toggleOpen={toggleClose}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    setLoader={setLoader}
                  />
                )}
                {type === 'clientOffice' && (
                  <ApplyExternalQuota
                    toggleOpen={toggleClose}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    setLoader={setLoader}
                    hoursType="clientOffice"
                  />
                )}
                {type === 'workTravel' && (
                  <ApplyExternalQuota
                    toggleOpen={toggleClose}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    setLoader={setLoader}
                    hoursType="workTravel"
                  />
                )}
              </Dialog>
            </div>
          </Box>
        </>
      )}
    </div>
  );
};

export default EmployeePrivilegesComponent;
